import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { bibleService } from '../../../services/bible';
import { telegramService } from '../../../services/telegram';
import { t } from '../../../i18n/uiText';
import { bfHaptic } from './BfHooks';
import BfSheet from './BfSheet';
import BfTranslationSwitcher from './BfTranslationSwitcher';
import { BfIconLink } from './BfIcons';

/**
 * BfParallelVerses — параллельные места в premium bottom sheet (BF design).
 */
export default function BfParallelVerses({
  verses = [],
  mainVerse,
  bookName,
  chapter,
  translation = 'synodal',
  onClose,
  onTranslationChange,
}) {
  const navigate = useNavigate();
  const [books, setBooks] = useState([]);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    bibleService.getBooks(translation).then(setBooks);
  }, [translation]);

  const findBookId = (name) => {
    const book = books.find((b) => b.name === name);
    return book ? book.id : null;
  };

  const getLocalizedBookName = (bookId, fallbackName) => {
    const book = books.find((item) => item.id === bookId);
    return book?.name || fallbackName;
  };

  const handleGoToVerse = (e, verse) => {
    e.stopPropagation();
    e.preventDefault();
    bfHaptic('light');
    const id = verse.bookId || findBookId(verse.bookName);
    if (id) {
      navigate(`/${translation}/${id}/${verse.chapter}/${verse.verse}`);
    }
  };

  const handleCopyLink = async () => {
    if (!mainVerse) return;
    const currentBookId = findBookId(bookName);
    if (!currentBookId) return;

    const isTelegram = telegramService.isTelegramWebApp;
    let link = '';
    if (isTelegram) {
      const botUsername = 'VerbumBibleBot';
      link = `https://t.me/${botUsername}/app?startapp=${currentBookId}_${chapter}_${mainVerse.verse}_parallel`;
    } else {
      link = `${window.location.origin}/${translation}/${currentBookId}/${chapter}/${mainVerse.verse}/parallel`;
    }

    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(link);
        setToast(t('linkCopied'));
        setTimeout(() => setToast(null), 2200);
      } else if (isTelegram) {
        telegramService.showAlert(`${t('linkLabel')}:\n${link}\n\n${t('copyManually')}`);
      }
    } catch {
      if (isTelegram) {
        telegramService.showAlert(`${t('linkLabel')}:\n${link}\n\n${t('copyManually')}`);
      }
    }
  };

  const count = verses?.length ?? 0;

  return (
    <>
      {toast && (
        <div className="bf-par-toast" role="status">
          {toast}
        </div>
      )}
      <BfSheet
        title={t('parallels')}
        titleMark
        onClose={onClose}
        ariaLabel={t('parallels')}
        headerClassName="bf-sheet__header--parallels"
        rightActions={
          <>
            {onTranslationChange && (
              <BfTranslationSwitcher
                currentTranslation={translation}
                onChange={onTranslationChange}
              />
            )}
            <button
              type="button"
              className="bf-icon-btn"
              onClick={handleCopyLink}
              aria-label={t('copyLink')}
              title={t('copyLink')}
            >
              <BfIconLink />
            </button>
          </>
        }
      >
        {mainVerse && (
          <div className="bf-par-ref">
            <div className="bf-par-ref__label">
              {bookName} {chapter}:{mainVerse.verse}
            </div>
            <div className="bf-par-ref__quote">«{mainVerse.text}»</div>
          </div>
        )}

        {count > 0 && (
          <div className="bf-par-divider" aria-hidden="true">
            <span className="bf-par-divider__text">
              {t('parallels')} ({count})
            </span>
          </div>
        )}

        {count === 0 ? (
          <p className="bf-par-empty">{t('parallels')}</p>
        ) : (
          <ul className="bf-par-list">
            {verses.map((verse, idx) => {
              const bookId = verse.bookId || findBookId(verse.bookName);
              const localizedBookName = getLocalizedBookName(bookId, verse.bookName);
              return (
                <li key={`${bookId}-${verse.chapter}-${verse.verse}-${idx}`} className="bf-par-card">
                  <div className="bf-par-card__head">
                    <div className="bf-par-card__ref">
                      <span className="bf-par-card__dot" aria-hidden="true" />
                      <span className="bf-par-card__ref-text">
                        {localizedBookName} {verse.chapter}:{verse.verse}
                      </span>
                    </div>
                    {bookId && (
                      <button
                        type="button"
                        className="bf-par-open"
                        onClick={(e) => handleGoToVerse(e, verse)}
                      >
                        {t('openPassage')}
                      </button>
                    )}
                  </div>
                  <p className="bf-par-card__text">{verse.text}</p>
                </li>
              );
            })}
          </ul>
        )}
      </BfSheet>
    </>
  );
}
